import { useMemo } from 'react'
import { Accessibility, ArrowRight } from 'lucide-react'
import type { CanvasBlock } from '@/lib/cms/card-registry'
import { getCardDef } from '@/lib/cms/card-registry'

/* ── Props ───────────────────────────────────────────── */

interface A11yCheckerProps {
  block: CanvasBlock | null
  onGoToProperty?: (section: string) => void
}

/* ── Check result ────────────────────────────────────── */

interface A11yCheck {
  status: 'pass' | 'warn' | 'fail'
  title: string
  desc: string
  fixSection?: string
}

const STATUS_ICONS: Record<string, string> = {
  pass: '✓',
  warn: '⚠',
  fail: '✕',
}

/* ── Audit logic ─────────────────────────────────────── */

function runChecks(block: CanvasBlock): A11yCheck[] {
  const checks: A11yCheck[] = []
  const def = getCardDef(block.type)

  // 1. Alt text on images
  if (block.type === 'image-block' || block.type === 'gallery-image') {
    const alt = block.data?.alt?.trim()
    if (!alt || alt === 'Image description') {
      checks.push({
        status: 'fail',
        title: 'Missing alt text',
        desc: 'Images must have descriptive alt text for screen readers.',
        fixSection: 'content',
      })
    } else if (alt.length < 10) {
      checks.push({
        status: 'warn',
        title: 'Alt text may be too short',
        desc: `Current: "${alt}". Consider a more descriptive alternative.`,
        fixSection: 'content',
      })
    } else {
      checks.push({
        status: 'pass',
        title: 'Alt text present',
        desc: `"${alt}"`,
      })
    }
  }

  // 2. Color contrast (simplified check)
  if (block.styles?.textColor && block.styles?.backgroundColor) {
    const tc = block.styles.textColor
    const bg = block.styles.backgroundColor
    // Simple luminance heuristic
    const isLightText = tc.includes('ff') || tc.includes('white') || tc === '#ffffff'
    const isDarkBg = bg.includes('00') || bg.includes('black') || bg === '#000000'
    const isGoodContrast = (isLightText && isDarkBg) || (!isLightText && !isDarkBg)

    checks.push({
      status: isGoodContrast ? 'pass' : 'warn',
      title: 'Color contrast',
      desc: isGoodContrast
        ? 'Text and background colors appear to have sufficient contrast.'
        : 'Text may have insufficient contrast against the background. Check WCAG AA ratio (4.5:1).',
      fixSection: 'style',
    })
  }

  // 3. Heading hierarchy
  if (block.type === 'heading') {
    const level = block.data?.level || 'h2'
    checks.push({
      status: level === 'h1' ? 'warn' : 'pass',
      title: 'Heading level',
      desc: level === 'h1'
        ? 'Multiple H1 tags can harm SEO. Consider using H2 or lower.'
        : `Using ${level.toUpperCase()} — good heading hierarchy.`,
      fixSection: 'content',
    })
  }

  // 4. Link text quality
  if (block.data?.cta_text) {
    const cta = block.data.cta_text.toLowerCase()
    const vague = ['click here', 'read more', 'learn more', 'here']
    if (vague.includes(cta)) {
      checks.push({
        status: 'warn',
        title: 'Vague link text',
        desc: `"${block.data.cta_text}" doesn't describe the link destination. Use descriptive text.`,
        fixSection: 'content',
      })
    } else {
      checks.push({
        status: 'pass',
        title: 'Descriptive link text',
        desc: `"${block.data.cta_text}"`,
      })
    }
  }

  // 5. Touch target size
  if (block.styles?.fontSize && block.styles.fontSize < 14) {
    checks.push({
      status: 'warn',
      title: 'Small font size',
      desc: `${block.styles.fontSize}px may be difficult to read. Minimum recommended: 14px.`,
      fixSection: 'style',
    })
  }

  // Default pass if no issues found
  if (checks.length === 0) {
    checks.push({
      status: 'pass',
      title: 'No issues found',
      desc: `${def?.label || block.type} passes basic accessibility checks.`,
    })
  }

  return checks
}

/* ── Score calculation ───────────────────────────────── */

function getScore(checks: A11yCheck[]): { score: number; label: string; color: string } {
  const total = checks.length
  const passes = checks.filter(c => c.status === 'pass').length
  const fails = checks.filter(c => c.status === 'fail').length
  const score = Math.round((passes / total) * 100)

  if (fails > 0) return { score, label: 'Issues Found', color: 'var(--editor-danger)' }
  if (score < 100) return { score, label: 'Warnings', color: 'var(--editor-warning)' }
  return { score, label: 'All Clear', color: 'var(--editor-success)' }
}

/* ── Component ───────────────────────────────────────── */

export default function A11yChecker({ block, onGoToProperty }: A11yCheckerProps) {
  const checks = useMemo(() => block ? runChecks(block) : [], [block])
  const { score, label: scoreLabel, color: scoreColor } = useMemo(() => getScore(checks), [checks])

  if (!block) {
    return (
      <div className="editor-empty-state">
        <Accessibility size={24} className="editor-empty-state-icon" />
        <div className="editor-empty-state-text">Select a block to audit</div>
        <div className="editor-empty-state-hint">Check contrast, alt text, headings, and more</div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Score header */}
      <div style={{
        padding: '12px var(--editor-panel-pad)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        borderBottom: '1px solid var(--editor-border-dim)',
      }}>
        <div style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: `2px solid ${scoreColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--editor-font-mono)',
          fontSize: 'var(--editor-font-size)',
          fontWeight: 600,
          color: scoreColor,
        }}>
          {score}
        </div>
        <div>
          <div style={{ fontSize: 'var(--editor-font-size)', fontWeight: 500 }}>{scoreLabel}</div>
          <div style={{ fontSize: 'var(--editor-font-size-xs)', color: 'var(--editor-text-dim)' }}>
            {checks.length} check{checks.length !== 1 ? 's' : ''} run
          </div>
        </div>
      </div>

      {/* Check results */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {checks.map((check, i) => (
          <div key={i} className="editor-a11y-item">
            <div className="editor-a11y-icon" data-status={check.status}>
              {STATUS_ICONS[check.status]}
            </div>
            <div className="editor-a11y-detail">
              <div className="title">{check.title}</div>
              <div className="desc">{check.desc}</div>
            </div>
            {check.fixSection && onGoToProperty && (
              <button
                className="editor-btn editor-btn-ghost editor-btn-icon editor-btn-sm"
                onClick={() => onGoToProperty(check.fixSection!)}
                title="Go to property"
              >
                <ArrowRight size={10} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
